import type { PageLoad } from './$types';
import { slugToStateAbbr, createServerApiClient } from '$lib/api-client';
import {
	getGameGamesGameIdGetOptions,
	getLatestReportGamesGameIdReportsLatestGetOptions
} from '$lib/api/@tanstack/svelte-query.gen';

export const load: PageLoad = async ({ params, fetch, parent }) => {
	const { id, slug } = params;
	const { queryClient } = await parent();

	const idInt = parseInt(id);

	if (isNaN(idInt)) {
		return {
			status: 404
		};
	}

	const serverApiClient = createServerApiClient(fetch);

	try {
		// Prefetch game and report data using TanStack Query
		await Promise.all([
			queryClient.prefetchQuery(
				getGameGamesGameIdGetOptions({
					client: serverApiClient,
					path: { game_id: idInt }
				})
			),
			queryClient.prefetchQuery(
				getLatestReportGamesGameIdReportsLatestGetOptions({
					client: serverApiClient,
					path: { game_id: idInt }
				})
			)
		]);

		// Get the prefetched data from query cache
		const gameApiData = queryClient.getQueryData(
			getGameGamesGameIdGetOptions({
				client: serverApiClient,
				path: { game_id: idInt }
			}).queryKey
		);

		const reportApiData = queryClient.getQueryData(
			getLatestReportGamesGameIdReportsLatestGetOptions({
				client: serverApiClient,
				path: { game_id: idInt }
			}).queryKey
		);

		if (!gameApiData || !reportApiData) {
			throw new Error('Failed to fetch required data');
		}

		// Transform game data to match old structure
		const transformedGame = {
			name: gameApiData.game_name,
			game_id: gameApiData.game_number,
			id: gameApiData.id,
			image_urls: gameApiData.images?.map((img: any) => img.url) || [],
			description: null, // Not available in new API
			how_to_play: null, // Not available in new API
			updated_at: reportApiData.fetched_at,
			price: gameApiData.ticket_price,
			ticket_value: reportApiData.ticket_value,
			calculated_odds: gameApiData.odds_of_winning,
			num_tx_initial: gameApiData.total_tickets,
			num_tx_remain: reportApiData.tickets_remaining,
			percent_tickets_sold: reportApiData.percent_tickets_remaining
				? (100 - reportApiData.percent_tickets_remaining) / 100
				: null, // Convert to decimal
			expected_value_initial: reportApiData.initial_expected_value,
			expected_value: reportApiData.current_expected_value,
			prizes: reportApiData.prize_reports.map((pr: any) => ({
				prize: pr.prize_tier.prize_description,
				value: pr.prize_tier.prize_value,
				available: pr.prizes_remaining,
				claimed: pr.prizes_claimed
			})),
			state: slug,
			// Additional fields that might be used
			value_initial: gameApiData.total_prize_value,
			value_remain: reportApiData.total_prize_value_remaining
		};

		// Transform gameData using report API data directly - no calculations needed!
		const transformedGameData = {
			name: gameApiData.game_name,
			state: slugToStateAbbr(slug),
			// Map report API fields to old field names
			score: reportApiData.score || Math.round((reportApiData.current_expected_value || 0) * 100), // Convert to percentage or fallback to current EV
			numTxRemaining: reportApiData.tickets_remaining,
			price: gameApiData.ticket_price,
			costToBuyRemainingTx: reportApiData.cost_to_buy_all_remaining,
			valueRemainingPrizes: reportApiData.total_prize_value_remaining,
			prizesExceedingCost:
				(reportApiData.total_prize_value_remaining || 0) -
				(reportApiData.cost_to_buy_all_remaining || 0),
			valuePerTx: reportApiData.profit_per_ticket,
			txSoldPerDay: reportApiData.avg_daily_sales,
			start: null, // Not available in report
			end: null, // Not available in report
			largestPrizeClaimed: reportApiData.largest_prize_claimed_since_last_report,
			grandPrizeValue: Math.max(
				...reportApiData.prize_reports.map((pr: any) => pr.prize_tier.prize_value)
			),
			grandPrizeLastClaimed: reportApiData.last_grand_prize_claim_date,
			txSinceLastGrandPrize: reportApiData.tickets_sold_since_last_grand_prize,
			totalNumGrandPrize: reportApiData.grand_prizes_remaining,
			txPerGrandPrize: reportApiData.expected_grand_prize_frequency
		};

		const meta = {
			title: `${reportApiData.grand_prizes_remaining || 0} Grand Prizes remain. Today's Scratch-off Odds for ${gameApiData.game_name} - ${slugToStateAbbr(slug)} Lottery | Dr. Lotto`,
			description: `What is the best scratch off ticket to buy? See the odds and analysis for the game ${gameApiData.game_name} from the ${slugToStateAbbr(slug)} scratch off lottery.`,
			keywords: `${gameApiData.game_name}, ${slugToStateAbbr(slug)}, lotto, scratchoff, scratch off odds`
		};

		return {
			gameData: transformedGameData,
			game: transformedGame,
			meta
		};
	} catch (error) {
		console.error(`Failed to fetch data for game ${id}:`, error);

		// Return fallback structure for graceful degradation
		return {
			gameData: null,
			game: null,
			meta: {
				title: `Game ${id} - ${slugToStateAbbr(slug)} Lottery - Dr. Lotto`,
				description: `Game details for ${slugToStateAbbr(slug)} lottery game ${id}.`,
				keywords: `${slugToStateAbbr(slug)}, lotto, scratchoff, scratch off odds`
			},
			apiError: true,
			error: error instanceof Error ? error.message : 'Unknown error'
		};
	}
};
